import React, { useEffect, useRef } from 'react';

import Modal, { ModalBody, ModalHeader } from '@lightspeed/cirrus-modal';
import { Alert } from '@lightspeed/flame/Alert';
import { Badge } from '@lightspeed/flame/Badge';
import { Button } from '@lightspeed/flame/Button';
import { Card, CardSection } from '@lightspeed/flame/Card';
import { Checkbox } from '@lightspeed/flame/Checkbox';
import { Box, Flex } from '@lightspeed/flame/Core';
import { Group } from '@lightspeed/flame/Group';
import { Input } from '@lightspeed/flame/Input';
import { Progress } from '@lightspeed/flame/Progress';
import { Heading4, Text, TextContent, TextLink } from '@lightspeed/flame/Text';
import axios from 'axios';
import uniq from 'lodash/uniq';

import { createReactRenderer } from 'helpers/react-renderer';

import MigrationErrorsTable from './components/MigrationErrorsTable';
import { MigrationToolBox, MigrationToolCardHeading } from './css/migrationToolStyles.ts';

const MigrationTool = ({ accountId = 0 }) => {
  const [domainName, setDomainName] = React.useState('');
  const [authToken, setAuthToken] = React.useState('');
  const [autoRefresh, setAutoRefresh] = React.useState(false);
  const [overallProgressCurrent, setOverallProgressCurrent] = React.useState(0);
  const [overallProgressTotal, setOverallProgressTotal] = React.useState(100);
  const [migrationProgressCurrent, setMigrationProgressCurrent] = React.useState(0);

  const [migrationOverallStatus, setMigrationOverallStatus] = React.useState('Unknown');
  const [migrationOverallStatusDisplayType, setMigrationOverallStatusDisplayType] =
    React.useState('default');
  const [errors, setErrors] = React.useState([]);
  const autoRefreshTimer = useRef(null);
  const [loadingStatus, setLoadingStatus] = React.useState('completed');
  const [showInfo, setShowInfo] = React.useState(false);
  const [showError, setShowError] = React.useState(false);
  const [alertMsg, setAlertMsg] = React.useState('');
  const [isStartMigrationButtonDisabled, setIsStartMigrationButtonDisabled] = React.useState(false);
  const [isInvalidateJobModalOpen, setIsInvalidateJobModalOpen] = React.useState(false);

  useEffect(() => {
    refreshStatus();
  }, []);

  const startBulkMigration = async () => {
    const response = await axios.get(
      `/admin/vendmigration/start?accountId=${accountId}&domainName=${domainName}&authToken=${authToken}`
    );

    const json = response?.data;

    if (response.status === 200) {
      setAlertMsg('Migration started');
      setShowInfo(true);
      setIsStartMigrationButtonDisabled(true);
    } else if (response.status === 422) {
      const errorString = json.errors.map((item) => item.detail).join(',');
      setAlertMsg(errorString);
      setShowError(true);
    } else {
      const errorString = json;
      setAlertMsg(errorString);
      setShowError(true);
    }
  };

  const handleDomainNameChange = (event) => {
    setDomainName(event.target.value);
  };

  const handleAuthTokenChange = (event) => {
    setAuthToken(event.target.value);
  };

  const refreshStatus = async () => {
    setLoadingStatus('loading');

    const response = await axios.get(`/admin/vendmigration/status?accountId=${accountId}`);
    const json = response?.data;

    switch (json.jobStatus) {
      case 'running':
        setMigrationOverallStatusDisplayType('info');
        setIsStartMigrationButtonDisabled(true);
        break;
      case 'success':
        setMigrationOverallStatusDisplayType('success');
        setIsStartMigrationButtonDisabled(false);
        break;
      case 'failure':
        setMigrationOverallStatusDisplayType('danger');
        setIsStartMigrationButtonDisabled(false);
        break;
      default:
        setMigrationOverallStatusDisplayType('info');
        setIsStartMigrationButtonDisabled(false);
    }

    if (json.domainName) {
      setDomainName(json.domainName);
    }
    if (json.authToken) {
      setAuthToken(json.authToken);
    }

    setOverallProgressCurrent(json.overallProgressCurrent);
    setOverallProgressTotal(json.overallProgressTotal);
    setMigrationProgressCurrent(json.migrationProgressCurrent);
    setMigrationOverallStatus(json.jobStatus);

    setErrors(
      json.errors.map((item) => {
        return {
          id: item.id,
          entityType: item.entity_type,
          description: item.error,
        };
      })
    );

    setLoadingStatus('completed');
  };

  const handleAutoRefreshCheckbox = (event) => {
    setAutoRefresh(event.target.checked);

    if (event.target.checked) {
      autoRefreshTimer.current = setInterval(refreshStatus.bind(this), 5000);
    } else {
      clearInterval(autoRefreshTimer.current);
    }
  };

  const invalidateJobs = () => {
    const formData = new FormData();
    formData.append('accountId', accountId);
    try {
      axios.post('/admin/vendmigration/invalidate', formData);
    } catch (err) {
      console.log(err);
      throw new Error('Cannot invalidate jobs');
    }
    setIsInvalidateJobModalOpen(false);
    setAlertMsg('Migration jobs were invalidated');
    setShowInfo(true);
    refreshStatus();
  };

  const handleRetry = (selectedItems) => {
    const formData = new FormData();
    formData.append('accountId', accountId);
    formData.append(
      'entities',
      uniq(selectedItems.map((id) => errors.find((item) => item.id === id)?.id)).join(',')
    );
    formData.append('domainName', domainName);
    formData.append('authToken', authToken);
    try {
      axios.post('/admin/vendmigration/retry', formData);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
      throw new Error('Cannot retry jobs');
    }

    setAlertMsg('The selected jobs retried');
    setShowInfo(true);
  };

  return (
    <React.Fragment>
      {showError ? (
        <Alert
          type="danger"
          title="Error"
          onClose={() => {
            setAlertMsg('');
            setShowError(false);
          }}
        >
          {alertMsg}
        </Alert>
      ) : null}
      {showInfo ? (
        <Alert
          type="info"
          title="Information"
          onClose={() => {
            setAlertMsg('');
            setShowInfo(false);
          }}
        >
          {alertMsg}
        </Alert>
      ) : null}
      <Modal data-test="invalidate-modal" isOpen={isInvalidateJobModalOpen}>
        <ModalHeader onCloseClicked={() => setIsInvalidateJobModalOpen(false)}>
          <Text bodyLarge>Invalidate Migration Jobs</Text>
        </ModalHeader>
        <ModalBody>
          <div
            style={{ width: '420px' }}
            className="cirrus cr-flex cr-width-full cr-justify-between cr-flex-col"
          >
            <Text p={1} mb={2}>
              This action will invalidate all migration jobs and set them to Failed. Please use with
              caution and only if you are sure the jobs have been already failed and not running
              anymore
            </Text>
            <Button
              data-test="invalidate-btn-modal"
              variant="danger"
              onClick={() => invalidateJobs()}
            >
              Invalidate
            </Button>
          </div>
        </ModalBody>
      </Modal>
      <Flex m={2} flexWrap="wrap">
        <MigrationToolCardHeading>Migration Controls</MigrationToolCardHeading>
        <MigrationToolBox>
          <Card>
            <CardSection>
              <Input
                label="Domain name of the X-Series Account:"
                placeholder="Domain name"
                value={domainName}
                onChange={handleDomainNameChange}
              />
            </CardSection>
            <CardSection>
              <Input
                label="Auth token for the X-Series Account:"
                placeholder="Auth token"
                value={authToken}
                onChange={handleAuthTokenChange}
              />
            </CardSection>
            <CardSection>
              <Button
                data-test="start-migration-btn"
                disabled={isStartMigrationButtonDisabled}
                variant="secondary"
                fill
                onClick={() => startBulkMigration()}
              >
                Start Bulk Migration
              </Button>
              <Button
                ml={2}
                data-test="invalidate-job-btn"
                disabled={!isStartMigrationButtonDisabled}
                variant="danger"
                fill
                onClick={() => setIsInvalidateJobModalOpen(true)}
              >
                Invalidate Migration Jobs
              </Button>
            </CardSection>
          </Card>
        </MigrationToolBox>
      </Flex>
      <Flex m={2} flexWrap="wrap">
        <MigrationToolCardHeading>Migration Status</MigrationToolCardHeading>
        <MigrationToolBox>
          <Card>
            <CardSection>
              <Group>
                <Badge type={migrationOverallStatusDisplayType}>{migrationOverallStatus}</Badge>
                <Button
                  data-test="refresh-status-btn"
                  variant="primary"
                  fill
                  onClick={() => refreshStatus()}
                >
                  Refresh
                </Button>
                <Checkbox
                  label="Enable auto refresh"
                  checked={autoRefresh}
                  onChange={handleAutoRefreshCheckbox}
                />
              </Group>
            </CardSection>
            <CardSection>
              <Text size="small" mb={1}>
                Work progress
              </Text>
              <Progress
                type="static"
                current={overallProgressCurrent}
                total={overallProgressTotal}
              />
              <Text size="small" mt={2} mb={1}>
                Successfully Migrated
              </Text>
              <Progress
                type="static"
                current={migrationProgressCurrent}
                total={overallProgressTotal}
              />
            </CardSection>
          </Card>
        </MigrationToolBox>
      </Flex>
      <Flex m={2} flexWrap="wrap">
        <MigrationToolCardHeading>Migration Errors</MigrationToolCardHeading>
        <MigrationToolBox>
          <MigrationErrorsTable
            loadingStatus={loadingStatus}
            errors={errors}
            handleRetry={handleRetry}
          />
        </MigrationToolBox>
      </Flex>
    </React.Fragment>
  );
};

/**
 * Prepares React component to be mounted, in this case the renderer expects
 * a `<div data-react-root="Example" data-react-props='{}'></div>` element.
 * See `js/apps/helpers/react-renderer.js` to see how it works.
 */
const RenderedComponent = createReactRenderer({
  component: MigrationTool,
  selector: '[data-react-root="MigrationTool"]',
});

let currentId;

history.onChangeState(
  { name: 'system.views.account', tab: 'migrate', id: undefined },
  ({ record_id: id }) => {
    currentId = id;
    RenderedComponent.mount();
  },
  ({ record_id: id }) => {
    if (currentId === id) return;
    RenderedComponent.unmount();
  }
);

export default MigrationTool;
